import { useEffect, useState, Fragment, useRef } from "react";
import styles from "./LoanApproval.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { IoIosArrowBack, IoIosArrowRoundForward } from "react-icons/io";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { useTheme } from "@mui/material/styles";

// components

// methods
import useResponse from "../../hooks/useResponse";
import AfterOnboardAPI from "../../services/AfterOnboardAPI";
import FinboxAPI from "../../services/finbox.api";
import { addAccountId, addUpgradeLoanAmount, changeLoanApproved, setStepperDisable } from "../../redux/slices/userSlice";
import { useNavigate } from "react-router-dom";
import { googleEvent_LOJ091, googleEvent_LOJ094, googleEvent_LOJ226 } from "../../lib/google-analytics";
import { pixelEvents } from "../../lib/pixel-events";
import { addLoanAmount } from "../../redux/slices/registerSlice";
import EMIDetails from "./components/EMIDetails/EMIDetails";
import { CustomButton } from "../../components";
import EMIsAPI from "../../services/emi-api";
import { handlehideContextualLoader, handleShowContextualLoader } from "../../utils/contextualLoaderTrigger";
import ApprovedBankAccount from "./components/ApprovedBankAccount/ApprovedBankAccount";
import { loanPurposeListItem } from "../../Constants/constant";
import api from "../../utils/api";
import { getEnvironment } from "../../utils/environment";
import { MdModeEdit, MdClose } from "react-icons/md";
import { ORDER_ID_LOCAL_STORAGE_KEY } from "../../Constants/constant";
import { clevertap_sv_loanApproval, clevertap_loan_approved_back_clk, clevertap_loan_approved_continue_clk, clevertap_sel_your_loan_upgrade_amount_clk, clevertap_drow_down_approved_pl_screen, clevertap_drow_down_approved_pl_screen_click } from "../../lib/clevertap-events";
import Swal from "sweetalert2";
import { dayDiff } from "../../utils/utils";

export default function LoanApproval() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [bankData, setBankData] = useState(null);
  const [loanPurpose, setLoanPurpose] = useState("");
  const [toggleDrawer, setToggleDrawer] = useState(false);
  const { API_URL } = getEnvironment();
  const { accountId } = useSelector((state) => state.user);
  const { selectedLoan } = useSelector((state) => state.register);
  const isCTPageLoadEventTrigger = useRef(false);

  const dispatch = useDispatch();
  const theme = useTheme();

  // const location = useLocation();
  const navigate = useNavigate();

  const emiData = selectedLoan || {};

  const { confirmBankAccountAPI } = new AfterOnboardAPI();
  const { finboxcreateUrlAPI } = new FinboxAPI();
  const { paydayToEMIConversionAPI } = new EMIsAPI();
  const prevIdxRef = useRef(window.history.state?.idx);
  const emi_plan = localStorage.getItem("emi_plan") || "NA";
  const offer_type = localStorage.getItem("offer_type") || "NA";

  const { handleStep } = useResponse();

  useEffect(() => {
    dispatch(changeLoanApproved(true));
    dispatch(setStepperDisable(true));
    handleFetchAPI();
  }, [dispatch]);

  useEffect(() => {
    // Rekyc Check
    if (localStorage.getItem("reKyc") === "1") {
      Swal.fire({
        icon: "success",
        text: "Your KYC details have been updated.",
        timer: 8000,
        showConfirmButton: true,
        timerProgressBar: true,
      });

      localStorage.removeItem("reKyc");
    }

    localStorage.removeItem(ORDER_ID_LOCAL_STORAGE_KEY);

    // Clevertap Event
    if (!isCTPageLoadEventTrigger.current) {
      clevertap_sv_loanApproval();
      clevertap_drow_down_approved_pl_screen({
        loan_amount: selectedLoan?.AmountDetails?.Amount || "N/A",
      });
      isCTPageLoadEventTrigger.current = true;
    }

    return () => {
      const currentIdx = window.history.state?.idx;
      if (prevIdxRef.current !== undefined && currentIdx < prevIdxRef.current) {
        clevertap_loan_approved_back_clk();
      }
    };
  }, []);

  const handleFetchAPI = async () => {
    const { loanApprovedAPI } = new AfterOnboardAPI();
    const res = await loanApprovedAPI(emiData);
    if (res && res.statusCode === 200) {
      setData(res.data);
      setLoanPurpose(res.data.loanPurpose);
      if (res.data.totalAmount) {
        googleEvent_LOJ091();
        pixelEvents.approvedProcess();
      }

      dispatch(addLoanAmount(res.data.totalAmount));
    }
    setLoading(false);
  };

  const handleUpgradeLoanAmount = async () => {
    setLoading(true);
    handleShowContextualLoader("Please wait...");
    const res = await finboxcreateUrlAPI();
    if (res) {
      dispatch(addUpgradeLoanAmount());

      googleEvent_LOJ226();
      window.location.href = res.data.redirect_url;
    }
    handlehideContextualLoader();
    setLoading(false);
    clevertap_sel_your_loan_upgrade_amount_clk();
  };

  const handlePaydayToEMIConversion = async () => {
    setLoading(true);
    const res = await paydayToEMIConversionAPI(emiData);

    if (res.statusCode === 200 && data.leadType !== "Repeat Case") {
      handleStep(accountId);
    }
    setLoading(false);
  };

  const handleConfirmBankAccount = async () => {
    const { mandateId, accountID } = bankData;

    const res = await confirmBankAccountAPI(mandateId, accountID);

    if (res && res.statusCode === 200) {
      dispatch(addAccountId(accountID));

      if (res?.data?.emdVerified) {
        handleStep(accountID);
        return;
      }

      if (res?.data?.route) {
        navigate(res.data.route);
        return;
      }

      handleStep(accountID);
    }
  };

  const handleUpdateLoanPurpose = async () => {
    try {
      const endpoint = API_URL + "/customers/incompleteDetailsUpdate";
      const req = {
        purposeloan: loanPurpose,
        leadID: Number(localStorage.getItem("leadID")),
        repeat: true,
      };

      const { data } = await api.put(endpoint, req);
    } catch (error) {
      console.log(error);
    }
  };

  if (!data) return null;

  const processingFee = () => {
    return (
      <div className="fw-600">
        {data?.isSpecialOffer ? <strike className={styles.strikAmount}>₹{data?.processingAmountBeforeDiscount?.toLocaleString("en-IN")}</strike> : null}
        -₹{data?.processingAmount?.toLocaleString("en-IN")}
      </div>
    );
  };
  const gstOnProcessingFee = () => {
    return (
      <div className="fw-600">
        {data?.isSpecialOffer ? <strike className={styles.strikAmount}>₹{data?.gstOfAdminFeeBeforeDiscount?.toLocaleString("en-IN")}</strike> : null}
        -₹{data?.gstOfAdminFee?.toLocaleString("en-IN")}
      </div>
    );
  };

  const calculateInterest = (principal, roi, days) => {
    if (!principal || !roi || !days) {
      Swal.fire({
        icon: "error",
        title: "We couldn't process your request. Some required information is missing. Please try again.",
      });
      return null;
    }

    if (!Number.isFinite(Number(principal)) || !Number.isFinite(Number(roi)) || !Number.isFinite(Number(days))) {
      return null;
    }

    return Math.round((Number(principal) * Number(roi) * Number(days)) / 100);
  };

  return (
    <div className={styles.loanApprovalStyle}>
      <div>
        {/* Top Header */}
        <div className={styles.topHeader}>
          {/* <div className={styles.backButton} onClick={() => (window.location.href = "/dashboard")}>
            <IoIosArrowBack size={34} color="#ffffff" />
          </div> */}
          <div className={styles.message}>
            <p className={styles.firstText}>
              <span className="fw-600">Special Offer!</span> Only For You
            </p>
          </div>
          <div className={styles.bgImage} />
        </div>

        {/* Loan Approved */}
        <div className={styles.loanApproved}>
          <h3>Loan Approved</h3>
          <div className={styles.images}>
            <img src="/images/hand-holding.svg" alt="" />
          </div>
          <p>You have been approved for a loan of amount</p>
          <h2>₹{data?.totalAmount.toLocaleString("en-IN")}</h2>

          <div className={styles.amountDetails}>
            <div>Approved amount</div>
            <div className="fw-700">₹{data?.totalAmount.toLocaleString("en-IN")}</div>
          </div>
          <div className={styles.amountDetails}>
            <div>Processing</div>
            {Object.keys(data).length ? processingFee() : null}
          </div>
          <div className={styles.amountDetails}>
            <div>GST on Processing fee</div>
            {Object.keys(data).length ? gstOnProcessingFee() : null}
          </div>
          <div className={`${styles.amountDetails} ${styles.finalAmount}`}>
            <div>Final loan amount</div>
            <div className="fw-600">₹{data?.finalAmount.toLocaleString("en-IN")}</div>
          </div>

          {data?.isLoanAmountUpgradable && emiData.ProductID === 2 && (
            <div className={styles.upgradeAmount} onClick={() => handleUpgradeLoanAmount()}>
              Upgrade my Loan Amount
            </div>
          )}
        </div>

        {/* Bank Account details */}
        <ApprovedBankAccount leadType={data.leadType} setBankData={setBankData} />

        {/* Loan Purpose */}
        {data?.leadType === "Repeat Case" && data?.loanPurpose ? (
          <div className={styles.loanPurpose}>
            <h4>Loan Purpose</h4>
            <div>
              {loanPurpose} <MdModeEdit className={styles.editIcon} size={20} onClick={() => setToggleDrawer(!toggleDrawer)} />
            </div>
          </div>
        ) : null}

        {/* Repayment Plan */}
        <div className={styles.repaymentPlan}>
          <h4>Repayment plan</h4>

          {emiData.ProductID === 1 ? (
            <EMIDetails emiData={data?.emiStructure} />
          ) : (
            <div className={styles.repaymentBlock}>
              <div className={styles.left}>
                <div className={styles.title}>Repayment Date</div>
                <div className={styles.value}>
                  {new Date(data?.repayDate).toDateString()} <span className={styles.days}>{data?.tenure} days</span>
                </div>
              </div>
              <div className={styles.repaymentBlockRow}>
                <div className={styles.title}>Principal</div>
                <div className={styles.value}>₹{data?.totalAmount.toLocaleString("en-IN")}</div>
              </div>
              <div className={styles.repaymentBlockRow}>
                <div className={styles.title}>Interest</div>
                <div className={styles.value}>₹{calculateInterest(data?.totalAmount, data?.roi, dayDiff(data?.repayDate))?.toLocaleString("en-IN")}</div>
              </div>
              <hr className={styles.repaymentDivider} />
              <div className={styles.repaymentBlockRow}>
                <div className={styles.title}>Repayment Amount</div>
                <div className={styles.value}>
                  {data?.isSpecialOffer ? <strike className={styles.strikAmount}>₹{data?.totalRepaymentAmountBeforeDiscount.toLocaleString("en-IN")}</strike> : null}₹{data?.repayAmount.toLocaleString("en-IN")}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Total Amount */}
      <div className={styles.totalAmount}>
        <div>
          <p>Total</p>
          <h5>₹{data?.totalAmount.toLocaleString("en-IN")}</h5>
          <span className={styles.taxText}>Inclusive of all taxes</span>
        </div>
        <div>
          <CustomButton
            floating={false}
            text="Continue"
            endIcon={<IoIosArrowRoundForward size={32} />}
            onClick={() => {
              const isRepeat = localStorage.getItem("aadhaar_vkyc_repeat") === "true";
              if (isRepeat) {
                localStorage.removeItem("aadhaar_vkyc_repeat");
                navigate("/enter-aadhar");
                return;
              }

              if (data.loanPurpose !== loanPurpose) {
                handleUpdateLoanPurpose();
              }
              if (emiData.ProductID === 1) {
                handlePaydayToEMIConversion();
              } else {
                if (data.leadType !== "Repeat Case" && !bankData) handleStep(accountId);
              }

              if (bankData) {
                handleConfirmBankAccount();
              }

              dispatch(setStepperDisable(false));

              // Events
              googleEvent_LOJ094();
              clevertap_loan_approved_continue_clk();
              clevertap_drow_down_approved_pl_screen_click({
                amount: data?.totalAmount || "N/A",
                final_amount: data?.finalAmount || "N/A",
                repay_amount: data?.repayAmount || "N/A",
                repay_date: data?.repayDate || "N/A",
                tenure: data?.tenure || "N/A",
              });
            }}
          />
        </div>
      </div>
      <Fragment>
        <SwipeableDrawer
          className={styles.loanPurposeBottomSheet}
          anchor="bottom"
          open={toggleDrawer}
          sx={{
            "& .MuiDrawer-paper": {
              maxWidth: 480,
              width: "100%",
              margin: "0 auto",
              left: "0",
              [theme.breakpoints.up("sm")]: {
                left: 0,
              },
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <div className={styles.title}>
            <h4>Select Loan Purpose</h4>
            <MdClose size={24} className={styles.closeIcon} onClick={() => setToggleDrawer(false)} />
          </div>
          {loanPurposeListItem.map((item, index) => (
            <div
              key={index}
              className={`${styles.listItem} ${item === loanPurpose ? styles.selected : ""}`}
              onClick={() => {
                setLoanPurpose(item);
                setToggleDrawer(false);
              }}
            >
              {item}
            </div>
          ))}
        </SwipeableDrawer>
      </Fragment>
    </div>
  );
}
